/**
 * HexResources component - displays hex resource values.
 * Styled as a Pip-Boy inspired green digital display panel.
 * Resources are 5 simplified categories: Tea, Wood, Mystical, Medicinal, Edible.
 */

interface ResourceData {
    tea: number;
    wood: number;
    mystical: number;
    medicinal: number;
    edible: number;
}

interface Props {
    resources: ResourceData;
    /** Compact mode shows only non-zero resources */
    compact?: boolean;
    /** Show header title */
    showHeaders?: boolean;
}

// Resource display configuration
const RESOURCE_CONFIG = {
    tea: {
        label: 'TEA',
        code: 'TEA',
    },
    wood: {
        label: 'WOOD',
        code: 'WOD',
    },
    mystical: {
        label: 'MYSTICAL',
        code: 'MYS',
    },
    medicinal: {
        label: 'MEDICINAL',
        code: 'MED',
    },
    edible: {
        label: 'EDIBLE',
        code: 'EDB',
    },
} as const;

type ResourceKey = keyof typeof RESOURCE_CONFIG;

/**
 * Scanline effect component for CRT look.
 */
function ScanlineOverlay() {
    return (
        <div
            className="absolute inset-0 pointer-events-none opacity-10"
            style={{
                backgroundImage: 'repeating-linear-gradient(0deg, transparent 0px, transparent 1px, rgba(0,0,0,0.3) 1px, rgba(0,0,0,0.3) 2px)',
            }}
        />
    );
}

/**
 * Displays hex resources in a Pip-Boy inspired digital panel.
 */
export function HexResources({ resources, compact = false, showHeaders = true }: Props) {
    // Get list of resources to display
    const resourceKeys = Object.keys(RESOURCE_CONFIG) as ResourceKey[];

    // In compact mode, only show non-zero resources
    const resourcesToShow = compact
        ? resourceKeys.filter((key) => resources[key] > 0)
        : resourceKeys;

    // Check if there are any resources to display
    const hasAnyResources = resourceKeys.some((key) => resources[key] > 0);

    if (compact && !hasAnyResources) {
        return (
            <div
                className="relative rounded-lg overflow-hidden p-3"
                style={{
                    background: 'linear-gradient(145deg, #0D2818 0%, #0A1F14 50%, #071A10 100%)',
                    border: '3px solid #1A3D28',
                    boxShadow: 'inset 0 0 20px rgba(0,40,20,0.5), 0 2px 4px rgba(0,0,0,0.3)',
                }}
            >
                <ScanlineOverlay />
                <p
                    className="relative text-sm font-mono italic text-center"
                    style={{ color: '#3D8B5A' }}
                >
                    NO RESOURCES
                </p>
            </div>
        );
    }

    return (
        <div
            className="relative rounded-lg overflow-hidden"
            style={{
                background: 'linear-gradient(145deg, #0D2818 0%, #0A1F14 50%, #071A10 100%)',
                border: '3px solid #1A3D28',
                boxShadow: 'inset 0 0 30px rgba(0,40,20,0.6), 0 2px 4px rgba(0,0,0,0.3)',
            }}
        >
            {/* CRT scanline effect */}
            <ScanlineOverlay />

            {/* Screen glow effect */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background: 'radial-gradient(ellipse at center, rgba(50,205,100,0.1) 0%, transparent 70%)',
                }}
            />

            {/* Panel header */}
            {showHeaders && (
                <div
                    className="relative px-3 py-2"
                    style={{
                        background: 'linear-gradient(180deg, rgba(30,100,60,0.4) 0%, rgba(20,70,40,0.2) 100%)',
                        borderBottom: '1px solid rgba(50,140,90,0.4)',
                    }}
                >
                    <h2
                        className="text-xs font-mono font-bold uppercase tracking-widest flex items-center gap-2"
                        style={{ color: '#4ADE80', textShadow: '0 0 10px rgba(74,222,128,0.5)' }}
                    >
                        <span className="inline-block w-2 h-2 rounded-full animate-pulse" style={{ background: '#4ADE80', boxShadow: '0 0 6px #4ADE80' }} />
                        YOUR RESOURCES
                    </h2>
                </div>
            )}

            {/* Resource readouts */}
            <div className="relative p-3 space-y-1">
                {resourcesToShow.map((key) => {
                    const config = RESOURCE_CONFIG[key];
                    const value = resources[key];

                    return (
                        <div
                            key={key}
                            className="flex items-center justify-between font-mono text-sm"
                            style={{
                                padding: '4px 8px',
                                background: 'rgba(0,30,15,0.5)',
                                borderRadius: '2px',
                                border: '1px solid rgba(50,140,90,0.2)',
                            }}
                        >
                            {/* Resource label */}
                            <span
                                className="font-medium tracking-wide"
                                style={{
                                    color: '#4ADE80',
                                    textShadow: '0 0 8px rgba(74,222,128,0.4)',
                                }}
                            >
                                {config.label}
                            </span>

                            {/* Dotted line filler */}
                            <span
                                className="flex-1 mx-2 border-b border-dotted"
                                style={{ borderColor: 'rgba(74,222,128,0.3)' }}
                            />

                            {/* Resource value - digital readout style */}
                            <span
                                className="font-bold tabular-nums min-w-[3ch] text-right"
                                style={{
                                    color: value > 0 ? '#7CFC00' : '#3D6B4A',
                                    textShadow: value > 0 ? '0 0 10px rgba(124,252,0,0.6)' : 'none',
                                }}
                            >
                                {value.toString().padStart(3, '0')}
                            </span>
                        </div>
                    );
                })}
            </div>

            {/* Bottom status bar */}
            <div
                className="relative px-3 py-1.5 flex items-center justify-between"
                style={{
                    background: 'linear-gradient(180deg, rgba(20,70,40,0.2) 0%, rgba(30,100,60,0.3) 100%)',
                    borderTop: '1px solid rgba(50,140,90,0.3)',
                }}
            >
                <span
                    className="text-xs font-mono"
                    style={{ color: '#3D8B5A' }}
                >
                    INVENTORY
                </span>
                <span
                    className="text-xs font-mono"
                    style={{ color: '#3D8B5A' }}
                >
                    {resourcesToShow.reduce((sum, key) => sum + resources[key], 0)} TOTAL
                </span>
            </div>

            {/* Corner decorations */}
            <div
                className="absolute top-1 left-1 w-2 h-2"
                style={{
                    borderTop: '2px solid rgba(74,222,128,0.3)',
                    borderLeft: '2px solid rgba(74,222,128,0.3)',
                }}
            />
            <div
                className="absolute top-1 right-1 w-2 h-2"
                style={{
                    borderTop: '2px solid rgba(74,222,128,0.3)',
                    borderRight: '2px solid rgba(74,222,128,0.3)',
                }}
            />
            <div
                className="absolute bottom-1 left-1 w-2 h-2"
                style={{
                    borderBottom: '2px solid rgba(74,222,128,0.3)',
                    borderLeft: '2px solid rgba(74,222,128,0.3)',
                }}
            />
            <div
                className="absolute bottom-1 right-1 w-2 h-2"
                style={{
                    borderBottom: '2px solid rgba(74,222,128,0.3)',
                    borderRight: '2px solid rgba(74,222,128,0.3)',
                }}
            />
        </div>
    );
}

export type { ResourceData };
